import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GeoSimCountryPreviewButton } from "@/components/admin/GeoSimCountryPreviewButton";
import { cn } from "@/lib/utils";
import { RefreshCw, Smartphone } from "lucide-react";
import { useMemo, useState } from "react";

type CountryOption = { code: string; name: string };

type CountryTemplateMobilePreviewProps = {
  countryCode: string;
  countries: CountryOption[];
  onCountryChange: (code: string) => void;
  effectiveTemplateLabel: string;
  videoOnInit: boolean;
};

function flag(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return "🏳️";
  const A = 0x1f1e6;
  return String.fromCodePoint(
    A + code.toUpperCase().charCodeAt(0) - 65,
    A + code.toUpperCase().charCodeAt(1) - 65,
  );
}

export function CountryTemplateMobilePreview({
  countryCode,
  countries,
  onCountryChange,
  effectiveTemplateLabel,
  videoOnInit,
}: CountryTemplateMobilePreviewProps) {
  const [iframeKey, setIframeKey] = useState(0);

  /** iframe SPA fallback only — edge real = GeoSim button below. */
  const previewUrlSpa = useMemo(() => {
    const params = new URLSearchParams({
      country: countryCode,
      site_settings_variant: "test",
    });
    return `/?${params.toString()}`;
  }, [countryCode]);

  const priorityOptions = useMemo(() => {
    const priority = new Set(["US", "MX", "ES", "BR", "GB", "DE", "FR", "CO", "CL", "AR"]);
    const first = countries.filter((c) => priority.has(c.code));
    const rest = countries.filter((c) => !priority.has(c.code));
    return [...first, ...rest];
  }, [countries]);

  return (
    <div className="rounded-2xl border bg-gradient-to-b from-muted/30 to-background p-4">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Smartphone className="h-4 w-4 text-primary" />
            Vista previa móvil
          </div>
          <p className="text-[11px] text-muted-foreground">
            {flag(countryCode)} {effectiveTemplateLabel}
            {videoOnInit ? " · video al abrir" : " · video tras clic"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={countryCode} onValueChange={onCountryChange}>
            <SelectTrigger className="h-10 min-w-[180px] flex-1 sm:flex-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[min(320px,60dvh)]">
              {priorityOptions.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {flag(c.code)} {c.name} ({c.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-10 w-10 shrink-0"
            onClick={() => setIframeKey((k) => k + 1)}
            title="Recargar preview"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <GeoSimCountryPreviewButton
            country={countryCode}
            source="country_templates_mobile_frame"
            className="h-10 shrink-0"
            size="sm"
            label="GeoSim"
            title={`Edge real · visitante ${countryCode} (mint gst, no SPA ?country=)`}
          />
        </div>
      </div>

      <div className="flex justify-center">
        <div
          className={cn(
            "relative overflow-hidden rounded-[2rem] border-[6px] border-zinc-800 bg-black shadow-2xl",
            "w-[min(300px,88vw)]",
          )}
          style={{ aspectRatio: "9 / 19.5" }}
        >
          <div
            className="pointer-events-none absolute left-1/2 top-0 z-10 h-5 w-24 -translate-x-1/2 rounded-b-2xl bg-zinc-900"
            aria-hidden
          />
          <iframe
            key={`${countryCode}-${iframeKey}`}
            title={`Preview home SPA ${countryCode} (fallback — usá GeoSim para edge)`}
            src={previewUrlSpa}
            className="h-full w-full border-0 bg-black"
            loading="lazy"
          />
        </div>
      </div>
      <p className="mt-3 text-center text-[10px] text-muted-foreground">
        El iframe es SPA <code>?country=</code> (borrador).{" "}
        <strong>GeoSim</strong> abre el edge real (template + CTA + pago del visitante).
      </p>
    </div>
  );
}